<template>
    <!-- Product Info -->
    <divider-generic
    dividerSize="medium"
    icon="coin-drop"
    :applyBorder="false"
    :title="t('credits.creditSolutions.subscribeProduct.step1.dividerProduct')"/>
    <text-block-generic :params="productNameParams"/>
    <container-collapsible
    startClosed
    doubleColumn
    :title="t('textBlocks.showDetails')"
    :openTitle="t('textBlocks.hideDetails')">
        <text-block-generic
        v-for="entry in detailsContainerEntries"
        :key="entry"
        :params="entry"
        :applyEllipsis="entry.applyEllipsis"
        :mask="entry.mask"/>
    </container-collapsible>
    <!-- Account Info -->
    <divider-generic
    icon="account-balance"
    dividerSize="medium"
    :title="t('credits.creditSolutions.subscribeProduct.step1.dividerAccount')"/>
    <dropdown-account
    id="accountId"
    :params="accountDropdownParams"
    :options="parsedAccountList"
    :selectedOption="formData.accountId"
    @clicked="emit('updateData', { fieldName: 'accountId', inputValue: $event })"/>
    <!-- Amount Info -->
    <divider-generic icon="bills" dividerSize="medium" :title="t('credits.creditSolutions.subscribeProduct.step1.dividerAmount')"/>
    <text-block-generic :params="plafondParams"/>
    <input-amount
    id="amount"
    :params="amountMaxMinParams"
    :inputValue="formData.amount"
    @input="[emit('updateData', { fieldName: 'amount', inputValue: $event }), clearFieldError('amountParams')]"
    @onBlur="amountValidation"/>
    <!-- Conditions Info -->
    <div class="conditions-wrapper">
        <div class="content">
            <!-- Divider -->
            <divider-generic icon="pencil-line" dividerSize="medium" :title="t('credits.creditSolutions.subscribeProduct.dividerLeftConditions')" />
            <!-- Note -->
            <text-block-generic :params="{ content: t('credits.creditSolutions.subscribeProduct.conditionsNote', { product: selectedProduct.name }) }" />
            <!-- Checkbox -->
            <selectors-checkbox
            :label="t('credits.creditSolutions.subscribeProduct.link', { link: selectedProduct.infoSheetUrl, target: '_blank' })"
            :selected="formData.conditions"
            :errorState="conditionsParams.errorState"
            :errorMessage="conditionsParams.errorMessage"
            @clicked="[emit('updateData', { fieldName: 'conditions', inputValue: !formData.conditions }), clearFieldError('conditionsParams')]"/>
            <div id="conditions"></div>
            <div class="conditions-sub-note">
                <text-block-generic :params="{ content: t('credits.creditSolutions.subscribeProduct.conditionsSubNote') }" />
            </div>
        </div>
    </div>
</template>

<script setup>
import DividerGeneric from '@/components/generic/dividers/Generic'
import TextBlockGeneric from '@/components/generic/textBlocks/Generic'
import ContainerCollapsible from '@/components/generic/containers/Collapsible'
import DropdownAccount from '@/components/generic/dropdowns/accounts/Account'
import InputAmount from '@/components/generic/inputs/Amount'
import SelectorsCheckbox from '@/components/generic/selectors/checkbox/Checkbox'

/* Vue & Assets */
import { ref, computed, watch } from 'vue'
import { amountFormat } from '@/assets/scripts/amounts'
import { getEmptyFieldsIdentifiers } from '@/assets/scripts/objects'
import { getElementOffsetTopPosition } from '@/assets/scripts/htmlManipulators'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

const props = defineProps({
    selectedProduct: Object,
    accountsBalances: Array,
    formData: Object,
    plafond: Object,
    mandatoryList: Array,
    showMandatoryErrors: Boolean,
	clearFormErrors: Boolean,
    currency: Object
})

/* Emmitters */
const emit = defineEmits(['scrollTo', 'updateData', 'resetShowMandatoryErrorsProp', 'formErrorsCleared'])

/* Data */
const productNameParams = ref({
    label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.name'),
    content: props.selectedProduct.name
})

const detailsContainerEntries = ref([
	{
		label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.description'),
		content: props.selectedProduct.description
	},
    {
		label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.duration'),
		content: props.selectedProduct.durationDescription
	},
    {
        label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.minimumAmount'),
		content: amountFormat(props.selectedProduct.minimumAmount, props.currency)
    },
	{
        label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.maximumAmount'),
		content: amountFormat(props.selectedProduct.maximumAmount, props.currency)
    },
    {
		label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.interestRateDescription'),
		content: props.selectedProduct.interestRateDescription
	},
    {
		label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.iva'),
		content: props.selectedProduct.interestRateVATDescription
	},
    {
		label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.overdraftRateDescription'),
		content: props.selectedProduct.overdraftRateDescription
	},
    {
		label: t('credits.creditSolutions.subscribeProduct.step1.detailsContainerEntries.effectiveAprDescription'),
		content: props.selectedProduct.effectiveAprDescription
	}
])

const accountDropdownParams = ref({
	labelText: t('credits.creditSolutions.subscribeProduct.step1.account'),
	mandatory: true,
	errorState: false,
    errorMessage: null
})

const parsedAccountList = computed(() => {
    return props.accountsBalances.filter(account => account.currency === props.selectedProduct.currency.name)
})

const plafondParams = computed(() => {
    return {
        label: t('credits.creditSolutions.subscribeProduct.step1.plafond'),
        content: amountFormat(props.plafond.amount, props.plafond.currency)
    }
})

const amountMaxMinParams = ref({
	labelText: t('credits.creditSolutions.subscribeProduct.step1.amount'),
	currency: props.currency,
	minValue: props.selectedProduct.minimumAmount,
	maxValue: props.selectedProduct.maximumAmount,
    mandatory: true,
	errorState: false,
    errorMessage: null
})

const amountValidation = () => {
    if (parseInt(props.formData.amount) > props.plafond.amount) {
        amountMaxMinParams.value.errorState = true
        amountMaxMinParams.value.errorMessage = t('credits.creditSolutions.subscribeProduct.step1.exceedPlafond')
        return
    }

    if (!props.formData.amount) return

    if (parseInt(props.formData.amount) === 0) {
        amountMaxMinParams.value.errorState = true
        amountMaxMinParams.value.errorMessage = t('invalidAmount')
    } else if (props.formData.amount < props.selectedProduct.minimumAmount || props.formData.amount > props.selectedProduct.maximumAmount) {
        amountMaxMinParams.value.errorState = true
        amountMaxMinParams.value.errorMessage = t('credits.creditSolutions.subscribeProduct.step1.amountErrorMessage')
    }
}

const conditionsParams = ref({
    errorState: false,
    errorMessage: null
})

/**********************************************************************
*                                                                     *
*                        MANDATORY FIELDS LOGIC                       *
*                                                                     *
***********************************************************************/
const allFormFields = ref({
	accountParams: accountDropdownParams.value,
    amountParams: amountMaxMinParams.value,
    conditionsParams: conditionsParams.value
})

watch(() => props.showMandatoryErrors, (newValue, oldValue) => {
	if (newValue) showMandatoryErrorsHandler()
    emit('resetShowMandatoryErrorsProp')
})

const showMandatoryErrorsHandler = () => {
	const mandatoryFieldsEmptyList = getEmptyFieldsIdentifiers(props.formData, props.mandatoryList)

    if (mandatoryFieldsEmptyList.length) emit('scrollTo', getElementOffsetTopPosition(mandatoryFieldsEmptyList[0]))

    if (props.formData.amount < props.selectedProduct.minimumAmount || props.formData.amount > props.selectedProduct.maximumAmount) {
        emit('scrollTo', getElementOffsetTopPosition('amount'))
    }

    if (!props.formData.conditions) {
        conditionsParams.value.errorMessage = t('mandatoryFieldError')
        conditionsParams.value.errorState = true
        emit('scrollTo', getElementOffsetTopPosition('conditions'))
    }

    mandatoryFieldsEmptyList.forEach(fieldName => {
        allFormFields.value[`${fieldName}Params`].errorMessage = t('mandatoryFieldError')
        allFormFields.value[`${fieldName}Params`].errorState = true
    })
}
/**********************************************************************
*                                                                     *
*                          CLEAR ERRORS LOGIC                         *
*                                                                     *
***********************************************************************/
watch(() => props.clearFormErrors, (newValue, oldValue) => {
    clearFormErrors()
    emit('formErrorsCleared')
})

const clearFieldError = (fieldName) => {
    allFormFields.value[fieldName].errorMessage = null
    allFormFields.value[fieldName].errorState = false
}

const clearFormErrors = () => {
    for (var fieldName in allFormFields.value) {
        clearFieldError(fieldName)
    }
}

emit('scrollTo', 0)
</script>
<style lang="scss" scoped>
.conditions-wrapper {
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;

    margin-top: 16px;
    .content {
        width: 100%;
        :deep(.text-blocks-wrapper) {
            margin-bottom: 16px;

            .content-wrapper {
                .content {
                    font-size: 15px;
                }
            }
        }
        .conditions-sub-note {
            width: 400px;
            max-width: 100%;

            :deep(.text-blocks-wrapper) {
                margin-top: 27px;
                .content-wrapper {
                    .content {
                        font-size: 12px;
                        color: var(--secondary-dark-2);
                    }
                }
            }
        }
    }

    &.mobile {
        padding: 16px 16px 24px 16px;
        border-radius: 12px;

        box-shadow: 0 3px 6px 1px rgba(0, 50, 90, 0.16);

        .content {
            :deep(.divider-generic-wrapper) {
                padding-top: 0;
            }
        }
    }
}
</style>
